import React from 'react';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { NOTE_HISTORY_RICH_CLASS, sanitizeHistoryHtml } from './leadHistoryHtml';

const COLLAPSED_MAX_HEIGHT_PX = 96;

/** Collapsible amber note body (Pipedrive-style), matching activity notes. */
export function LeadNoteHistoryCardBody({
  snippet,
  className = 'bg-note-surface px-3 py-2.5',
}: {
  snippet: string;
  className?: string;
}) {
  const safe = sanitizeHistoryHtml(snippet);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [overflows, setOverflows] = React.useState(false);

  React.useLayoutEffect(() => {
    setExpanded(false);
  }, [snippet]);

  React.useEffect(() => {
    const el = contentRef.current;
    if (!el || expanded) return;

    const measure = () => {
      setOverflows(el.scrollHeight > COLLAPSED_MAX_HEIGHT_PX + 1);
    };

    measure();
    if (!('ResizeObserver' in window)) {
      return;
    }

    const ro = new ResizeObserver(() => {
      // Async content (z.B. Bilder) kann nach dem ersten Render nachladen.
      // ResizeObserver stellt sicher, dass wir dann die Overflow-States aktualisieren.
      measure();
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [snippet, expanded]);

  if (!snippet.trim()) return null;

  const showToggle = overflows || expanded;

  const toggle = () => setExpanded((v) => !v);

  const onSurfaceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if ((e.target as HTMLElement).closest('button')) return;
    if (window.getSelection()?.toString().trim()) return;
    if (!showToggle) return;
    toggle();
  };

  return (
    <div
      className={`relative ${className} ${!expanded && overflows ? 'cursor-pointer' : ''}`}
      onClick={onSurfaceClick}
    >
      {showToggle ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="absolute right-2 top-2 z-10 rounded-md p-1 text-app-text-subtle hover:bg-amber-500/15 hover:text-app-text-muted"
          title={expanded ? 'Notiz einklappen' : 'Notiz vollständig anzeigen'}
          aria-label={expanded ? 'Notiz einklappen' : 'Notiz vollständig anzeigen'}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronsDownUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      ) : null}
      <div className="relative">
        <div
          ref={contentRef}
          className={`${NOTE_HISTORY_RICH_CLASS} ${showToggle ? 'pr-7' : ''}`}
          style={expanded ? undefined : { maxHeight: COLLAPSED_MAX_HEIGHT_PX, overflow: 'hidden' }}
          dangerouslySetInnerHTML={{ __html: safe }}
        />
        {!expanded && overflows ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-note-surface to-transparent"
          />
        ) : null}
      </div>
    </div>
  );
}
